import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Building2, Users, Clock, ClipboardList, PackageCheck, TriangleAlert, CircleDollarSign, Hourglass, ChevronRight } from "lucide-react";
import { rpc } from "@/lib/supabase";
import { PageHeader, Stat, Loader } from "@/components/kit";
import { formatCOP } from "@/lib/format";

export default function AdminDashboard() {
  const [o, setO] = useState(null);
  const [stuck, setStuck] = useState(null); // null = cargando, "error" = fallo aislado
  useEffect(() => {
    rpc("admin_get_overview").then(setO).catch(() => setO({}));
  }, []);
  const loadStuck = useCallback(() => {
    rpc("admin_get_stuck_services", { p_threshold_minutes: 30 })
      .then((d) => setStuck((d || []).length))
      .catch(() => setStuck("error"));
  }, []);
  useEffect(() => {
    loadStuck();
    const t = setInterval(loadStuck, 30000);
    return () => clearInterval(t);
  }, [loadStuck]);
  if (!o) return <Loader />;
  const stuckCount = typeof stuck === "number" ? stuck : 0;
  return (
    <div>
      <PageHeader title="Panel de administración" subtitle="Visión global de la operación de RAPIFAST." testId="admin-dashboard" />
      <Link
        to="/admin/stuck"
        data-testid="stuck-alert-card"
        className={`mb-4 flex items-center justify-between gap-4 rounded-2xl border p-5 shadow-lg transition-colors hover:bg-[#18181B] ${
          stuckCount > 0 ? "border-[#FF6A00]/60 bg-[#FF6A00]/5" : "border-[#27272A] bg-[#141416]"
        }`}
      >
        <div className="flex items-center gap-4">
          <div className={`grid h-11 w-11 place-items-center rounded-xl ${stuckCount > 0 ? "bg-[#FF6A00]/15 text-[#FF6A00]" : "bg-[#18181B] text-gray-400"}`}>
            <Hourglass size={20} />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Servicios atascados</div>
            <div className="text-sm text-gray-400">
              {stuck === "error"
                ? "No se pudo cargar el conteo"
                : stuckCount > 0
                ? "En búsqueda de conductor por más de 30 min"
                : "Sin servicios atascados"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {stuck === null ? (
            <span className="text-sm text-gray-500" data-testid="stuck-count-loading">…</span>
          ) : stuck === "error" ? (
            <span className="text-sm text-gray-500" data-testid="stuck-count-error">—</span>
          ) : (
            <span
              className={`font-heading text-3xl font-extrabold ${stuckCount > 0 ? "text-[#FF6A00]" : "text-gray-500"}`}
              data-testid="stuck-count"
            >
              {stuckCount}
            </span>
          )}
          <ChevronRight size={18} className="text-gray-500" />
        </div>
      </Link>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Negocios" value={o.businesses ?? 0} testId="admin-stat-businesses" />
        <Stat label="Conductores" value={o.drivers_total ?? 0} hint={`${o.drivers_pending ?? 0} pendientes`} testId="admin-stat-drivers" />
        <Stat label="Servicios totales" value={o.services_total ?? 0} testId="admin-stat-services" />
        <Stat label="Activos" value={o.services_active ?? 0} accent="#FF6A00" testId="admin-stat-active" />
        <Stat label="Entregados" value={o.services_delivered ?? 0} testId="admin-stat-delivered" />
        <Stat label="Incidencias abiertas" value={o.incidents_open ?? 0} accent="#F87171" testId="admin-stat-incidents" />
        <Stat label="GMV (entregado)" value={formatCOP(o.gmv)} testId="admin-stat-gmv" />
        <Stat label="Comisión (25%)" value={formatCOP(o.commission)} accent="#FF6A00" testId="admin-stat-commission" />
      </div>
    </div>
  );
}
